import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface RiskManagementTileFilter {
  IssueStatus?: string;
  IssueRating?: string;
  ActionPlanStatus?: string | string[];
  IssueCategory?: string;
}

interface ActionPlanTile {
  count: number;
  label: string;
  filterValue: string | string[];
}

@Component({
  standalone: true,
  selector: 'app-risk-management-dashboard',
  imports: [CommonModule],
  templateUrl: './risk-management-dashboard.component.html',
  styleUrl: './risk-management-dashboard.component.css',
})
export class RiskManagementDashboardComponent {
  @Output() tileClick = new EventEmitter<RiskManagementTileFilter>();

  issues: Array<{ count: number; label: string }> = [
    { count: 0, label: 'Open' },
    { count: 0, label: 'Closed' },
  ];
  totalIssues = 0;
  totalIssuesTooltipLines: string[] = [];

  private _issueOpen = 0;
  private _issueClosed = 0;

  issueRatings: Array<{ count: number; label: string; color: string; darkText: boolean }> = [
    { count: 0, label: 'Red', color: '#EB3323', darkText: false },
    { count: 0, label: 'Yellow', color: '#F6C143', darkText: true },
    { count: 0, label: 'Blue', color: '#93AAD7', darkText: true },
    { count: 0, label: 'Green', color: '#167C61', darkText: false },
  ];

  actionPlans: ActionPlanTile[] = [
    { count: 0, label: 'Open', filterValue: 'Open' },
    { count: 0, label: 'Open Ageing & Static', filterValue: 'Open-Ageing & Static' },
    { count: 0, label: 'Closed', filterValue: 'Closed' },
    { count: 0, label: 'Closed-Blue Not Tracked', filterValue: 'Closed-Blue Not Tracked' },
  ];
  totalActionPlans = 0;

  /** Action plan counts from GetReviewDashboardCounts. */
  @Input() set actionPlanCounts(value: any) {
    const d = Array.isArray(value) ? value[0] : value;
    if (!d) return;
    this.totalActionPlans = d.TotalActionPlans ?? 0;
    this.actionPlans = [
      { count: d['Open'] ?? 0,                    label: 'Open', filterValue: 'Open' },
      { count: d['Open-Ageing & Static'] ?? d['Open Ageing & Static'] ?? 0,    label: 'Open Ageing & Static',  filterValue: 'Open-Ageing & Static' },
      { count: d['Closed'] ?? 0,                  label: 'Closed', filterValue: 'Closed' },
      { count: d['Closed-Blue Not Tracked'] ?? 0, label: 'Closed-Blue Not Tracked', filterValue: 'Closed-Blue Not Tracked' },
    ];
  }

  /** Open/Closed issue totals from GetReviewDashboardCounts. */
  @Input() set issueCategory(value: any) {
    const d = Array.isArray(value) ? value[0] : value;
    if (!d) return;
    const open = d.Open ?? 0;
    const closed = d.Closed ?? 0;
    this.issues = [
      { count: open, label: 'Open' },
      { count: closed, label: 'Closed' },
    ];
    this.totalIssues = open + closed;
    this._issueOpen = open;
    this._issueClosed = closed;
    this.buildIssuesTooltip();
  }

  private buildIssuesTooltip(): void {
    const ratingLine = this.issueRatings
      .map(r => `${r.label}: ${r.count}`)
      .join('  ·  ');
    this.totalIssuesTooltipLines = [
      `Open: ${this._issueOpen}`,
      `Closed: ${this._issueClosed}`,
      ...(ratingLine ? [ratingLine] : []),
    ];
  }

  /** Issue rating counts from GetReviewDashboardCounts. */
  @Input() set issueRating(value: any[]) {
    const list = Array.isArray(value) ? value : [];
    if (!list.length) return;
    const styleByRating: Record<string, { color: string; darkText: boolean }> = {
      Red: { color: '#EB3323', darkText: false },
      Yellow: { color: '#F6C143', darkText: true },
      Blue: { color: '#93AAD7', darkText: true },
      Green: { color: '#167C61', darkText: false },
    };
    this.issueRatings = list.map(r => ({
      count: r.IssueRatingCount ?? 0,
      label: r.IssueRating,
      color: styleByRating[r.IssueRating]?.color ?? '#93AAD7',
      darkText: styleByRating[r.IssueRating]?.darkText ?? true,
    }));
    this.buildIssuesTooltip();
  }

  selectTotalIssues(): void {
    this.tileClick.emit({ IssueStatus: 'All' });
  }

  selectTotalActionPlans(): void {
    this.tileClick.emit({ ActionPlanStatus: 'All' });
  }

  selectIssueStatus(issueStatus: string): void {
    this.tileClick.emit({ IssueStatus: issueStatus });
  }

  selectIssueRating(issueRating: string): void {
    this.tileClick.emit({ IssueRating: issueRating });
  }

  selectActionPlanStatus(actionPlanStatus: string | string[]): void {
    this.tileClick.emit({ ActionPlanStatus: actionPlanStatus });
  }

}
